import { useQuery, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';

import { bankFeedApi } from 'domain/bankFeed/api';
import type { EndpointResponse } from 'services/api';
import { queryOptions, useApiMutation } from 'services/query';
import { handleErrorResponseWithToast } from 'lib/toasts';
import { bankFeedKeys } from './keys';

type UserPermissionsCache = EndpointResponse<typeof bankFeedApi.getUserPermissions>;

export const bankFeedUserPermissionsQuery = (companyId: string) =>
  queryOptions({
    queryKey: bankFeedKeys.userPermissions(companyId),
    queryFn: () => bankFeedApi.getUserPermissions(),
  });

export const useBankFeedUserPermissions = (companyId: string) =>
  useQuery(bankFeedUserPermissionsQuery(companyId));

export const useUpdateBankFeedUserPermissionMutation = (companyId: string) => {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: bankFeedApi.updateUserPermissions,
    // The POST returns the updated rows — merge them into the cache by user id
    // (avoids a redundant GET that would only re-fetch what we already have).
    onSuccess: (response) => {
      qc.setQueryData<UserPermissionsCache>(
        bankFeedKeys.userPermissions(companyId),
        produce((draft) => {
          if (!draft) return;
          const updated = new Map(response.items.map((row) => [row.user.id, row]));
          draft.items = draft.items.map((row) => updated.get(row.user.id) ?? row);
        }),
      );
    },
    onError: handleErrorResponseWithToast,
  });
};
